import React, { useState } from 'react';
import { Calendar, Download, CreditCard, Truck, CheckCircle, Package } from 'lucide-react';
import KPICard from '../components/KPICard';
import DataTable from '../components/DataTable';
import QueryInfoButton from '../components/QueryInfoButton';
import { useCardMetricsReport, useCardMetricsSummary } from '../hooks/useReports';
import { formatNumber, formatDate } from '../utils/formatters';
import { CARD_METRICS_QUERIES } from '../constants/queries';

export default function CardMetrics() {
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  const { data: reportData, isLoading: reportLoading } = useCardMetricsReport(
    dateRange.start,
    dateRange.end
  );
  const { data: summaryData, isLoading: summaryLoading } = useCardMetricsSummary();

  const summary = summaryData?.data?.[0] || {};

  const columns = [
    { key: 'date_day', label: 'Date', sortable: true },
    { key: 'tenant_name', label: 'Tenant', sortable: true },
    { key: 'cards_ordered_count', label: 'Cards Ordered', sortable: true, format: 'number' },
    { key: 'cards_shipped_count', label: 'Cards Shipped', sortable: true, format: 'number' },
    { key: 'cards_activated_count', label: 'Cards Activated', sortable: true, format: 'number' },
  ];

  const formattedData = reportData?.data?.map(row => ({
    ...row,
    date_day: formatDate(row.date_day),
  })) || [];

  const handleExport = () => {
    if (!reportData?.data) return;

    const headers = columns.map((col) => col.label).join(',');
    const rows = reportData.data.map((row) =>
      columns
        .map((col) => {
          const value = row[col.key];
          if (col.key === 'date_day') return formatDate(value);
          if (col.format === 'number') return formatNumber(value);
          return `"${value}"`;
        })
        .join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `card-metrics-${dateRange.start}-to-${dateRange.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = [
    {
      title: 'Total Cards Ordered',
      value: summary.total_cards_ordered,
      icon: Package,
      format: 'compact',
    },
    {
      title: 'Total Cards Shipped',
      value: summary.total_cards_shipped,
      icon: Truck,
      format: 'compact',
    },
    {
      title: 'Total Cards Activated',
      value: summary.total_cards_activated,
      icon: CheckCircle,
      format: 'compact',
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Card Metrics Report</h2>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">
            Daily card orders, shipments, and activations per tenant
          </p>
        </div>
        <QueryInfoButton
          title="Card Metrics Data Sources"
          queries={[
            CARD_METRICS_QUERIES.report,
            CARD_METRICS_QUERIES.summary,
          ]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {kpis.map((kpi, index) => (
          <KPICard key={index} {...kpi} loading={summaryLoading} />
        ))}
      </div>

      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Daily Card Data</h3>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="input w-40"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="input w-40"
              />
            </div>

            <button onClick={handleExport} className="btn-secondary">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        <DataTable
          data={formattedData}
          columns={columns}
          loading={reportLoading}
          emptyMessage="No card data available for the selected period"
        />
      </div>

      <div className="card bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900">About This Report</h4>
            <p className="text-sm text-amber-700 mt-1">
              This report shows daily card activity per tenant.
              <strong> Cards Ordered</strong> represents new card orders placed on that day.
              <strong> Cards Shipped</strong> shows cards that were shipped to members.
              <strong> Cards Activated</strong> shows cards that were activated by members.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
